/**
 * The visit itinerary's printable checklist (Arthur, 20 October): a document
 * of its own, opened from the planner and printed, so the app's header,
 * navigation and dark colours never reach paper.
 *
 * What it prints, and only this: the date and the visitor; for each building
 * its name, address and telephone; for each client the row number, the name,
 * a line for the room (a facility placement records none) and the visit: the
 * clients due first, marked "Due" with the visit due, then, when the stop is set
 * to see everyone here (A-#54), its residents up to date, marked "Up to date"
 * with their last visit; with tick boxes and room for notes. NO date of birth, diagnosis or
 * identifier, not even the client reference; and NO "Completed by / Name /
 * Date" sign-off block (Arthur's standing preference for printouts).
 *
 * It is built in memory and written into a blank window: nothing is in its
 * address and nothing is stored.
 *
 * Its look (Arthur, 23 October, A-#68): system sans-serif throughout; the
 * procura shield (the disc version, the approved icon's own paths, 3f1e848) and
 * the wordmark centred at the top of page 1 only, drawn inline, so the page
 * fetches nothing; Letter with half-inch margins; rows never split and the
 * table's header repeats on each page.
 */

export type ChecklistClient =
  | { name: string; room: string | null; status: 'due'; visitType: string }
  | { name: string; room: string | null; status: 'up to date'; lastVisit: string | null };
export interface ChecklistFacility {
  name: string;
  street: string | null;
  townLine: string | null;
  phone: string | null;
  clients: ChecklistClient[];
}
export interface Checklist { date: string; visitor: string; facilities: ChecklistFacility[] }

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "Tuesday, October 21, 2026": a calendar day, never an instant (CLAUDE.md, dates). */
export function longDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  // The day of the week of that calendar day: noon UTC on it is the same day everywhere it is read.
  const dow = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
  return `${DAYS[dow]}, ${MONTHS[m - 1]} ${d}, ${y}`;
}

/** "October 3, 2026": a calendar day, never an instant. */
export function shortDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** "Sep 28, 2026": the checklist's own short day, so the Visit column stays narrow. */
export function printDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return `${MONTHS[m - 1]!.slice(0, 3)} ${d}, ${y}`;
}

/** What a client's Visit cell says. */
export function visitCell(cl: ChecklistClient): string {
  return cl.status === 'due'
    ? `Due: ${cl.visitType}`
    : `Up to date: ${cl.lastVisit ? `last visit ${printDay(cl.lastVisit)}` : 'no visit on record'}`;
}

/**
 * One stop's part of the checklist, drawn ONLY from that building's audited answer (141): the due clients the planner
 * kept, in their order, then, when the stop is set to see everyone, every resident up to date. So nothing printed was
 * not listed, and a print needs no audit row of its own.
 */
export interface ChecklistStop {
  detail: {
    facility: { facility_name: string; street_address: string | null; city: string | null; state: string | null; zip: string | null; phone: string | null };
    up_to_date: { name: string; room: string | null; last_visit: string | null }[];
  };
  clients: { name: string; room: string | null; visit_type: string }[];
  everyone: boolean;
}
export function stopChecklist(s: ChecklistStop): ChecklistFacility {
  const f = s.detail.facility;
  return {
    name: f.facility_name,
    street: f.street_address,
    townLine: [f.city, [f.state, f.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null,
    phone: f.phone,
    clients: [
      ...s.clients.map((c): ChecklistClient => ({ name: c.name, room: c.room, status: 'due', visitType: c.visit_type })),
      ...(s.everyone ? s.detail.up_to_date.map((c): ChecklistClient => ({ name: c.name, room: c.room, status: 'up to date', lastVisit: c.last_visit })) : []),
    ],
  };
}

/** The shield's outline, from the approved icon (web/public/favicon.svg and the disc version, 3f1e848). */
const SHIELD = 'M32 5.5C31.76 5.6 31.09 5.85 30.54 6.11C29.99 6.37 29.32 6.78 28.71 7.08C28.1 7.39 27.49 7.67 26.88 7.94C26.27 8.2 25.66 8.42 25.06 8.67C24.45 8.91 23.84 9.18 23.23 9.4C22.62 9.62 22.01 9.83 21.4 10.01C20.79 10.19 20.18 10.35 19.57 10.5C18.96 10.64 18.35 10.74 17.74 10.86C17.14 10.98 16.53 11.1 15.92 11.23C15.31 11.35 14.7 11.49 14.09 11.59C13.48 11.69 12.97 11.71 12.26 11.84C11.55 11.96 10.23 12.24 9.83 12.32C9.83 12.93 9.8 14.96 9.83 15.98C9.85 16.99 9.91 17.6 9.95 18.41C9.99 19.23 10.03 20.04 10.07 20.85C10.11 21.66 10.13 22.48 10.19 23.29C10.25 24.1 10.35 24.91 10.43 25.73C10.52 26.54 10.56 27.35 10.68 28.16C10.8 28.97 10.98 29.79 11.17 30.6C11.35 31.41 11.53 32.22 11.77 33.04C12.02 33.85 12.34 34.66 12.63 35.47C12.91 36.28 13.18 37.1 13.48 37.91C13.79 38.72 14.07 39.53 14.46 40.35C14.84 41.16 15.33 41.97 15.8 42.78C16.26 43.6 16.73 44.41 17.26 45.22C17.79 46.03 18.33 46.84 18.96 47.66C19.59 48.47 20.28 49.28 21.03 50.09C21.79 50.91 22.58 51.72 23.47 52.53C24.36 53.34 25.32 54.15 26.4 54.97C27.47 55.78 28.99 56.81 29.93 57.4C30.86 57.99 31.65 58.32 32 58.5C32.35 58.32 33.14 57.99 34.07 57.4C35.01 56.81 36.53 55.78 37.6 54.97C38.68 54.15 39.64 53.34 40.53 52.53C41.42 51.72 42.21 50.91 42.97 50.09C43.72 49.28 44.41 48.47 45.04 47.66C45.67 46.84 46.21 46.03 46.74 45.22C47.27 44.41 47.74 43.6 48.2 42.78C48.67 41.97 49.16 41.16 49.54 40.35C49.93 39.53 50.21 38.72 50.52 37.91C50.82 37.1 51.09 36.28 51.37 35.47C51.66 34.66 51.98 33.85 52.23 33.04C52.47 32.22 52.65 31.41 52.83 30.6C53.02 29.79 53.2 28.97 53.32 28.16C53.44 27.35 53.48 26.54 53.57 25.73C53.65 24.91 53.75 24.1 53.81 23.29C53.87 22.48 53.89 21.66 53.93 20.85C53.97 20.04 54.01 19.23 54.05 18.41C54.09 17.6 54.15 16.99 54.17 15.98C54.2 14.96 54.17 12.93 54.17 12.32C53.77 12.24 52.45 11.96 51.74 11.84C51.03 11.71 50.52 11.69 49.91 11.59C49.3 11.49 48.69 11.35 48.08 11.23C47.47 11.1 46.86 10.98 46.26 10.86C45.65 10.74 45.04 10.64 44.43 10.5C43.82 10.35 43.21 10.19 42.6 10.01C41.99 9.83 41.38 9.62 40.77 9.4C40.16 9.18 39.55 8.91 38.94 8.67C38.34 8.42 37.73 8.2 37.12 7.94C36.51 7.67 35.9 7.39 35.29 7.08C34.68 6.78 34.01 6.37 33.46 6.11C32.91 5.85 32.24 5.6 32 5.5Z';

/**
 * The procura shield, the disc version (the large icon's: a white disc, the "p" in shield teal), inline. A print
 * window is a document of its own, so these are the brand's colours written out, not the app's tokens.
 */
export const LOGO_SVG =
  `<svg class="logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="procura">` +
  `<path d="${SHIELD}" fill="#0b2a35" stroke="#0b2a35" stroke-width="6.4" stroke-linejoin="miter" stroke-miterlimit="20"/>` +
  `<path d="${SHIELD}" fill="#006788" stroke="#ffffff" stroke-width="4" stroke-linejoin="miter" stroke-miterlimit="20"/>` +
  `<circle cx="32" cy="27.5" r="14" fill="#ffffff"/>` +
  `<g fill="none" stroke="#006788" stroke-width="5"><circle cx="32" cy="27.5" r="7.8"/>` +
  `<path d="M24.2 27.5V43.88" stroke-linecap="butt"/></g></svg>`;

/** The checklist's style. Sizes in points, as Arthur gave them; the column widths add to 100%. */
export const CHECKLIST_CSS =
  `@page{size:letter;margin:0.5in}` +
  `body{font-family:Arial,Helvetica,sans-serif;font-size:10pt;margin:0;color:#000}` +
  `.brand{text-align:center;margin:0 0 6pt}.logo{display:block;height:0.45in;width:auto;margin:0 auto}` +
  // The app header's wordmark face; a print window loads no font, so a plain sans stands in.
  `.wordmark{font-family:'Comfortaa',ui-rounded,'Segoe UI',system-ui,sans-serif;font-weight:300;font-size:14pt;` +
  `letter-spacing:0.02em;color:#0b2a35;margin-top:2pt}` +
  `h1{font-size:14pt;margin:0}h2{font-size:12pt;font-weight:bold;margin:12pt 0 1pt}` +
  `.meta{margin:2pt 0 0}.addr{font-size:9.5pt;margin:0 0 4pt}` +
  `table{width:100%;border-collapse:collapse;table-layout:fixed}` +
  `th,td{border:0.5pt solid #808080;padding:3pt 5pt;vertical-align:top;text-align:left;overflow-wrap:anywhere}` +
  `thead{display:table-header-group}th{background:#e8e8e8;font-weight:bold}td{height:24pt}` +
  // The row number never wraps: 4% is narrow, so its padding is too.
  `.n{padding:3pt 2pt;white-space:nowrap;overflow-wrap:normal;text-align:right}.box{white-space:nowrap}.line{display:inline-block;width:40pt;border-bottom:0.5pt solid #808080}` +
  `.more{margin:4pt 0 2pt}.lines{height:0.6in;border:0.5pt solid #808080}` +
  // Buildings flow on, so a long table crosses pages with its header repeated; but a building never starts at the
  // foot of a page (its heading and address stay with the table's first rows), and the notes box stays with its label.
  `h2,.addr,.more{break-after:avoid;page-break-after:avoid}.lines{break-inside:avoid}` +
  `tr{break-inside:avoid;page-break-inside:avoid}` +
  `*{-webkit-print-color-adjust:exact;print-color-adjust:exact}` +
  `@media screen{body{max-width:7.5in;margin:0.5in auto}}`;

const COLUMNS = `<colgroup><col style="width:4%"><col style="width:26%"><col style="width:12%">` +
  `<col style="width:20%"><col style="width:13%"><col style="width:25%"></colgroup>`;

export function checklistHtml(c: Checklist): string {
  let row = 0;
  const facilities = c.facilities.map((f) => {
    const rows = f.clients.map((cl) => {
      row += 1;
      return `<tr><td class="n">${row}</td><td>${esc(cl.name)}</td>` +
        `<td>${cl.room ? esc(cl.room) : '<span class="line"></span>'}</td>` +
        `<td>${esc(visitCell(cl))}</td>` +
        `<td class="box">&#9744; Seen<br>&#9744; Not seen</td>` +
        `<td class="notes"></td></tr>`;
    }).join('');
    return `<section>` +
      `<h2>${esc(f.name)}</h2>` +
      `<p class="addr">${[f.street, f.townLine].filter(Boolean).map((s) => esc(s!)).join(', ') || 'Address not on record'}` +
      ` &middot; Tel. ${f.phone ? esc(f.phone) : 'not on record'}</p>` +
      `<table>${COLUMNS}<thead><tr><th class="n">#</th><th>Client</th><th>Room / unit</th><th>Visit</th><th>Visited</th><th>Notes</th></tr></thead>` +
      `<tbody>${rows}</tbody></table>` +
      `<p class="more">Notes for this building:</p><div class="lines"></div>` +
      `</section>`;
  }).join('');
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Visit itinerary</title>` +
    `<style>${CHECKLIST_CSS}</style></head><body>` +
    // Once, at the top of page 1: in the body's flow, never a running header, so later pages do not repeat it.
    `<div class="brand">${LOGO_SVG}<div class="wordmark">procura</div></div>` +
    `<h1>Visit itinerary</h1>` +
    `<p class="meta">${esc(longDay(c.date))} &middot; Visitor: ${esc(c.visitor)}</p>` +
    facilities +
    `</body></html>`;
}
